import React from 'react';
import AuditionDetailRoles from './AuditionDetailRoles';
import AuditionDetailProducer from './AuditionDetailProducer';
import Icon from '../../components/common/Icon/Icon';
import styles from './AuditionDetailContent.module.css';

function Section({ title, children }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

function Checklist({ items }) {
  return (
    <ul className={styles.checklist}>
      {items.map((item, idx) => (
        <li key={idx} className={styles.checklistItem}>
          <span className={styles.checklistIcon}>
            <Icon name="check" size="16" />
          </span>
          <span className={styles.checklistText}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Process({ steps }) {
  return (
    <div className={styles.process}>
      {steps.map((s, idx) => (
        <React.Fragment key={s.step}>
          <div className={styles.processStep}>
            <span className={styles.processStepNumber}>{s.step}</span>
            <span className={styles.processStepLabel}>{s.label}</span>
          </div>
          {idx < steps.length - 1 && (
            <span className={styles.processArrow} aria-hidden="true">›</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * 실제로 열 수 있는 첨부파일만 남긴다.
 *
 * 서버가 빈 자리를 null로 채워 보내는 공고가 있다([null]). 그대로 그리면 주소에서
 * 파일 이름을 뽑다가 예외가 나고, 공고 상세가 통째로 빈 화면이 된다.
 */
function usableAttachments(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments.filter(url => typeof url === 'string' && url.trim());
}

function AuditionDetailContent({ activeTab, data, onRoleApply, appliedRoleIds, isExpired, onRoleDetail }) {
  if (activeTab === 'roles') {
    return <AuditionDetailRoles roles={data.roles} onRoleApply={onRoleApply} appliedRoleIds={appliedRoleIds} isExpired={isExpired} onRoleDetail={onRoleDetail} />;
  }

  if (activeTab === 'producer') {
    return (
      <AuditionDetailProducer
        producerInfo={data.producerInfo}
        companyId={data.company.id}
        companyName={data.company.name}
        isVerified={data.company.isVerified}
      />
    );
  }

  const attachments = usableAttachments(data.attachments);

  return (
    <div className={styles.content}>
      <Section title="작품 소개">
        {/* 빈 줄에서만 끊어 문단을 만들던 탓에, 엔터를 한 번 친 줄바꿈은 사라졌다.
            적은 그대로 보여준다 — 줄을 어떻게 나눌지는 쓴 사람이 정한다 */}
        <p className={styles.paragraph}>{data.description}</p>
      </Section>

      <Section title="지원 자격">
        <Checklist items={data.requirements} />
      </Section>

      <Section title="공고 절차">
        <Process steps={data.process} />
      </Section>

      {(data.additionalInfo || attachments.length > 0) && (
        <Section title="추가 안내사항">
          {data.additionalInfo && <p className={styles.paragraph}>{data.additionalInfo}</p>}

          {attachments.length > 0 && (
            <div className={styles.attachments}>
              <p className={styles.attachmentsTitle}>첨부파일</p>
              <ul className={styles.attachmentList}>
                {attachments.map((url, idx) => {
                  const fileName = decodeURIComponent(url.split('/').pop().split('?')[0]);
                  return (
                    <li key={idx} className={styles.attachmentItem}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.attachmentLink}
                        title={fileName}
                      >
                        {fileName}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

export default AuditionDetailContent;
